import { memo, useRef, useState, useCallback, useMemo, useEffect } from 'react'

import ActionButton from '../ActionButton'
import VolumeIcon from 'assets/svgr-icons/Volume.svg'
import VolumeMuteIcon from 'assets/svgr-icons/VolumeMute.svg'

import * as Styled from './styled'
import { T } from 'consts/translationKeys'
import { translate } from 'utils/translations'
import {
  DEFAULT_VOLUME,
  handleAudioVolumeChange,
  handleMediaVolumeChange
} from './utils'
import { useActiveItems } from '../../hooks/use-active-items'
import { getTimelineItemVolume } from '../../utils'

const VolumeControlButton = () => {
  const { activeMediaItems, activeAudioItems } = useActiveItems()
  const initialVolume = useMemo(() => {
    return Math.max(
      ...[...activeMediaItems, ...activeAudioItems].map(getTimelineItemVolume)
    )
  }, [activeMediaItems, activeAudioItems])

  const [isOpen, setIsOpen] = useState(false)
  const [volume, setVolume] = useState(initialVolume ?? DEFAULT_VOLUME)
  const [isMuted, setIsMuted] = useState(
    (initialVolume ?? DEFAULT_VOLUME) === 0
  )
  const volumeBeforeMuteRef = useRef(volume)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const newVolume = initialVolume ?? DEFAULT_VOLUME
    setVolume(newVolume)
    setIsMuted(newVolume === 0)
  }, [initialVolume])

  const onClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  const toggleFlyout = useCallback(() => {
    setIsOpen(prev => !prev)
  }, [])

  const applyVolume = useCallback(
    (newVolume: number) => {
      handleMediaVolumeChange(newVolume, activeMediaItems)
      handleAudioVolumeChange(newVolume, activeAudioItems)
    },
    [activeMediaItems, activeAudioItems]
  )

  const toggleMute = useCallback(() => {
    setIsMuted(prev => {
      if (!prev) {
        volumeBeforeMuteRef.current = volume
        setVolume(0)
        applyVolume(0)
      } else {
        const restored = volumeBeforeMuteRef.current || DEFAULT_VOLUME
        setVolume(restored)
        applyVolume(restored)
      }
      return !prev
    })
  }, [volume, applyVolume])

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = Number(event.target.value)
      setVolume(value)
      if (value > 0 && isMuted) {
        setIsMuted(false)
      } else if (value === 0 && !isMuted) {
        setIsMuted(true)
      }
    },
    [isMuted]
  )

  const handleMouseUp = useCallback(
    (volume: number) => {
      applyVolume(volume)
    },
    [applyVolume]
  )

  return (
    <>
      <ActionButton
        ref={buttonRef}
        icon={isMuted ? <VolumeMuteIcon /> : <VolumeIcon />}
        tooltipText={!isOpen ? translate(T.VOLUME) : ''}
        onClick={toggleFlyout}
      />
      <Styled.Flyout
        isOpen={isOpen}
        onClose={onClose}
        anchorEl={buttonRef.current}
        options={{
          position: 'top',
          extraOffset: 10
        }}
        bottomMargin={64}
      >
        <Styled.FlyoutContent>
          <Styled.VolumeControlContainer>
            <Styled.VolumeValue>{volume}%</Styled.VolumeValue>
            <Styled.RangeSlider
              minValue={0}
              maxValue={100}
              value={volume}
              onChange={handleChange}
              handleMouseUp={handleMouseUp}
            />
          </Styled.VolumeControlContainer>
          <Styled.MuteButton onClick={toggleMute} $isMuted={isMuted}>
            {isMuted ? <VolumeMuteIcon /> : <VolumeIcon />}
          </Styled.MuteButton>
        </Styled.FlyoutContent>
      </Styled.Flyout>
    </>
  )
}

export default memo(VolumeControlButton)
